<template>
    <div class="page honor-board-page">
        <transition name="fade-transition">
            <v-container v-if="!loading" :class="{ 'py-3 px-0': phoneOnly, 'py-6': pcOnly }">
                <div class="honor-board-header px-4 px-lg-0 mb-4 mb-lg-6">
                    <div class="d-flex align-center justify-space-between flex-wrap">
                        <div>
                            <h1 class="honor-board-title strong-text--text mb-1">لوحة الشرف</h1>
                            <p class="muted--text mb-0 details-text">
                                {{ hasFinishTime
                                    ? 'الترتيب حسب درجة الامتحان، وعند التعادل يُفضَّل الأسرع إن وُجد وقت التسليم'
                                    : 'الترتيب حسب درجة الامتحان' }}
                            </p>
                        </div>
                        <v-chip v-if="rankedStudents.length" color="primary" outlined class="mt-3 mt-lg-0">
                            {{ rankedStudents.length }} طالب
                        </v-chip>
                    </div>
                </div>

                <template v-if="rankedStudents.length">
                    <v-card v-if="pcOnly" flat class="bordered honor-board-table-card">
                        <v-simple-table class="honor-board-table">
                            <thead>
                                <tr>
                                    <th class="text-center" style="width: 72px">الترتيب</th>
                                    <th>الطالب</th>
                                    <th v-if="hasGovernorate">المحافظة</th>
                                    <th v-if="hasScores" class="text-center">الدرجة</th>
                                    <th v-if="hasFinishTime">وقت إنهاء الامتحان</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="student in rankedStudents" :key="student.id + '-' + student.rank">
                                    <td class="text-center">
                                        <span
                                            class="honor-rank"
                                            :class="rankClass(student.rank)"
                                        >{{ student.rank }}</span>
                                    </td>
                                    <td>
                                        <div class="d-flex align-center">
                                            <v-avatar size="44" class="me-3">
                                                <img :src="student.avatar" :alt="student.name" />
                                            </v-avatar>
                                            <div class="font-weight-bold strong-text--text">{{ student.name }}</div>
                                        </div>
                                    </td>
                                    <td v-if="hasGovernorate" class="text--text">{{ student.governorate }}</td>
                                    <td v-if="hasScores" class="text-center">
                                        <v-chip
                                            v-if="student.earnedMark != null && student.totalMark != null"
                                            small
                                            color="primary"
                                            class="font-weight-bold ltr-dir"
                                        >
                                            {{ student.earnedMark }}/{{ student.totalMark }}
                                        </v-chip>
                                    </td>
                                    <td v-if="hasFinishTime">
                                        <div v-if="student.finishTime || student.timeTaken != null" class="d-flex flex-column">
                                            <span v-if="student.finishTime" class="ltr-dir">
                                                {{ dateTimeFormat(student.finishTime, isTimestamp(student.finishTime)) }}
                                            </span>
                                            <span v-if="student.timeTaken != null" class="details-text muted--text">
                                                الوقت المستغرق: {{ formatSeconds(student.timeTaken) }}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card>

                    <v-list v-else class="py-0">
                        <template v-for="student in rankedStudents">
                            <v-list-item :key="student.id + '-' + student.rank" class="py-2 px-4">
                                <div class="honor-rank me-3" :class="rankClass(student.rank)">
                                    {{ student.rank }}
                                </div>
                                <v-list-item-avatar size="52">
                                    <img :src="student.avatar" :alt="student.name" />
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title class="strong-text--text font-weight-bold">
                                        {{ student.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle class="mt-1">
                                        <v-chip
                                            v-if="student.earnedMark != null && student.totalMark != null"
                                            label
                                            x-small
                                            color="primary"
                                            class="ltr-dir px-2 me-2 font-weight-bold"
                                        >
                                            {{ student.earnedMark }}/{{ student.totalMark }}
                                        </v-chip>
                                        <span v-if="student.governorate">{{ student.governorate }}</span>
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle v-if="student.finishTime || student.timeTaken != null" class="mt-1">
                                        <span v-if="student.finishTime" class="ltr-dir me-2">
                                            {{ dateTimeFormat(student.finishTime, isTimestamp(student.finishTime)) }}
                                        </span>
                                        <span v-if="student.timeTaken != null" class="muted--text">
                                            ({{ formatSeconds(student.timeTaken) }})
                                        </span>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider :key="'d-' + student.id + '-' + student.rank" inset style="opacity: 0.55" />
                        </template>
                    </v-list>
                </template>

                <div v-else class="pa-4 pa-lg-0">
                    <v-alert type="info" color="blue-grey" text class="mb-0">
                        <div class="details-text">لا يوجد طلاب في لوحة الشرف حالياً.</div>
                    </v-alert>
                </div>
            </v-container>
        </transition>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';
import { rankHonorStudents } from '@/helpers/honor-board';

export default {
    name: 'HonorBoard',
    data() {
        return {
            loading: false,
        };
    },
    computed: {
        honorPanel() {
            return this.homeData && this.homeData.honor_panel ? this.homeData.honor_panel : [];
        },
        rankedStudents() {
            return rankHonorStudents(this.honorPanel);
        },
        hasFinishTime() {
            return this.rankedStudents.some((student) => student.finishTime || student.timeTaken != null);
        },
        hasGovernorate() {
            return this.rankedStudents.some((student) => !!student.governorate);
        },
        hasScores() {
            return this.rankedStudents.some(
                (student) => student.earnedMark != null && student.totalMark != null
            );
        },
        homeData() {
            return this.$store.state.globalModule.homeData;
        },
        ...GlobalComputed,
    },
    watch: {
        loading(loading) {
            this.setLinearLoading(loading);
        },
    },
    methods: {
        ...GlobalMethods,
        rankClass(rank) {
            if (rank === 1) return 'honor-rank--gold';
            if (rank === 2) return 'honor-rank--silver';
            if (rank === 3) return 'honor-rank--bronze';
            return 'honor-rank--default';
        },
        isTimestamp(value) {
            return typeof value === 'number' || /^\d+$/.test(String(value));
        },
        formatSeconds(seconds) {
            let remaining = Number(seconds) * 1000;
            if (!remaining) {
                return '00:00';
            }
            const hours = Math.floor(remaining / (1000 * 60 * 60));
            const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
            const secs = Math.floor((remaining % (1000 * 60)) / 1000);
            const hoursText = hours < 10 ? `0${hours}` : hours;
            const minutesText = minutes < 10 ? `0${minutes}` : minutes;
            const secondsText = secs < 10 ? `0${secs}` : secs;
            return hours ? `${hoursText}:${minutesText}:${secondsText}` : `${minutesText}:${secondsText}`;
        },
        loadData() {
            if (this.loading) {
                return;
            }
            if (this.honorPanel && this.honorPanel.length) {
                return;
            }
            this.loading = true;
            this.$store.dispatch('loadHomeData')
                .then(() => {
                    this.loading = false;
                })
                .catch(() => {
                    this.showMsg('حدثت مشكلة أثناء تحميل لوحة الشرف');
                    this.loading = false;
                });
        },
    },
    mounted() {
        this.loadData();
    },
};
</script>

<style lang="scss" scoped>
.honor-board-title {
    font-size: 1.45rem;
    font-weight: 800;
    line-height: 1.35;
}

.honor-board-table-card {
    overflow: hidden;
    border-radius: 14px !important;
}

.honor-board-table {
    th {
        font-weight: 700 !important;
        white-space: nowrap;
    }
}

.honor-rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 34px;
    height: 34px;
    padding: 0 8px;
    border-radius: 999px;
    font-weight: 800;
    font-size: 0.9rem;
    line-height: 1;

    &--gold {
        background: linear-gradient(135deg, #f6d365, #fda085);
        color: #5b3b00;
    }

    &--silver {
        background: linear-gradient(135deg, #e8e8e8, #c0c0c0);
        color: #333;
    }

    &--bronze {
        background: linear-gradient(135deg, #f0c27b, #c68642);
        color: #4a2c0a;
    }

    &--default {
        background: rgba(0, 0, 0, 0.06);
        color: inherit;
    }
}
</style>
